import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiPencil, HiEye, HiTrash, HiPlus } from 'react-icons/hi';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import ConfirmationModal from '../components/ConfirmationModal';
import Tooltip from '../components/Tooltip';
import { getErrorMessage } from '../utils/errorHandler';
import { formatDate } from '../utils/dateFormatter';
import { API } from '../config';
import axios from 'axios';
import './Businesses.css';

const Businesses = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { success, error } = useNotification();
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState(null);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    adminUsername: '',
    adminEmail: '',
    adminPassword: '',
    adminPhone: ''
  });

  useEffect(() => {
    fetchBusinesses();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = businesses.filter(business =>
        business.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        business.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        business.phone?.includes(searchTerm)
      );
      setFilteredBusinesses(filtered);
    } else {
      setFilteredBusinesses(businesses);
    }
  }, [searchTerm, businesses]);

  const fetchBusinesses = async () => {
    try {
      let response;
      if (user.role === 'super_admin') {
        response = await axios.get(`${API}/businesses/all`);
      } else {
        response = await axios.get(`${API}/businesses/my-business`);
      }
      const businessData = response.data.business ? [response.data.business] : response.data.businesses || [];
      setBusinesses(businessData);
      setFilteredBusinesses(businessData);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (business) => {
    setEditingBusiness(business);
    setFormData({
      name: business.name,
      description: business.description || '',
      address: business.address || '',
      phone: business.phone || '',
      email: business.email || '',
      adminUsername: '',
      adminEmail: '',
      adminPassword: '',
      adminPhone: ''
    });
    setShowModal(true);
  };

  const handleView = (business) => {
    navigate(`/businesses/${business.id}/details`);
  };

  const handleDelete = (businessId) => {
    setBusinessToDelete(businessId);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/businesses/${businessToDelete}`);
      success('Business deleted successfully!');
      setShowConfirmModal(false);
      setBusinessToDelete(null);
      fetchBusinesses();
    } catch (err) {
      error(getErrorMessage(err));
      setShowConfirmModal(false);
      setBusinessToDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBusiness) {
        await axios.put(`${API}/businesses/${editingBusiness.id}`, {
          name: formData.name,
          description: formData.description,
          address: formData.address,
          phone: formData.phone,
          email: formData.email
        });
        success('Business updated successfully!');
      } else if (user.role === 'super_admin') {
        await axios.post(`${API}/businesses`, formData);
        success('Business created successfully!');
      }
      setShowModal(false);
      setEditingBusiness(null);
      setFormData({
        name: '',
        description: '',
        address: '',
        phone: '',
        email: '',
        adminUsername: '',
        adminEmail: '',
        adminPassword: '',
        adminPhone: ''
      });
      fetchBusinesses();
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  return (
    <Layout>
      <div className="businesses-page">
        <div className="page-header">
          <div>
            <h1>Businesses</h1>
            <p className="page-subtitle">Manage all businesses in the system</p>
          </div>
          {user.role === 'super_admin' && (
            <Tooltip text="Create a new business" position="bottom">
              <button onClick={() => setShowModal(true)} className="create-button">
                <HiPlus />
                <span>Create Business</span>
              </button>
            </Tooltip>
          )}
        </div>

        <div className="search-bar-container">
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search businesses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {loading ? (
          <Loader message="Loading businesses..." variant="block" />
        ) : (
          <div className="table-container">
            {businesses.length === 0 ? (
              <div className="empty-state">
                <p>No businesses found.</p>
                {user.role === 'super_admin' && (
                  <button onClick={() => setShowModal(true)} className="empty-state-button">
                    Create Your First Business
                  </button>
                )}
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Branches</th>
                    <th>Users</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBusinesses.map((business) => (
                    <tr key={business.id}>
                      <td className="code-cell">BUS-{String(business.id).padStart(3, '0')}</td>
                      <td className="font-semibold">{business.name}</td>
                      <td>{business.email || '—'}</td>
                      <td>{business.phone || '—'}</td>
                      <td>
                        <span 
                          className="badge clickable-badge" 
                          onClick={() => navigate(`/businesses/${business.id}/branches`)}
                          style={{ cursor: 'pointer' }}
                        >
                          {business.branches?.length || 0}
                        </span>
                      </td>
                      <td>
                        <span 
                          className="badge clickable-badge" 
                          onClick={() => navigate(`/businesses/${business.id}/users`)}
                          style={{ cursor: 'pointer' }}
                        >
                          {business.users?.filter(u => u.role === 'admin').length || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${business.is_active ? 'active' : 'inactive'}`}>
                          {business.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="created-at-cell">{formatDate(business.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <Tooltip text="Edit business" position="top">
                            <button 
                              className="action-btn edit-btn" 
                              onClick={() => handleEdit(business)}
                            >
                              <HiPencil />
                            </button>
                          </Tooltip>
                          <Tooltip text="View details" position="top">
                            <button 
                              className="action-btn view-btn" 
                              onClick={() => handleView(business)}
                            >
                              <HiEye />
                            </button>
                          </Tooltip>
                          <Tooltip text="Delete business" position="top">
                            <button 
                              className="action-btn delete-btn" 
                              onClick={() => handleDelete(business.id)}
                            >
                              <HiTrash />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingBusiness(null); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{editingBusiness ? 'Edit Business' : 'Create Business'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Business Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Tech Solutions Inc."
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g. A leading technology solutions provider"
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="e.g. 123 Main Street, City, Country"
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="e.g. +1-555-0100 or +92 300 1234567"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="e.g. contact@business.com"
                  />
                </div>
                {!editingBusiness && (
                  <>
                    <h3>Admin Details</h3>
                    <div className="form-group">
                      <label>Admin Username *</label>
                      <input
                        type="text"
                        value={formData.adminUsername}
                        onChange={(e) => setFormData({ ...formData, adminUsername: e.target.value })}
                        placeholder="e.g. hanz_latest23"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Admin Email (optional)</label>
                      <input
                        type="email"
                        value={formData.adminEmail}
                        onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                        placeholder="e.g. admin@business.com"
                      />
                    </div>
                    <div className="form-group">
                      <label>Admin Password *</label>
                      <input
                        type="password"
                        value={formData.adminPassword}
                        onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                        placeholder="Min. 6 characters"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Admin Phone</label>
                      <input
                        type="text"
                        value={formData.adminPhone}
                        onChange={(e) => setFormData({ ...formData, adminPhone: e.target.value })}
                        placeholder="e.g. +1-555-0101"
                      />
                    </div>
                  </>
                )}
                <div className="modal-actions">
                  <button type="button" onClick={() => { setShowModal(false); setEditingBusiness(null); }}>Cancel</button>
                  <button type="submit">{editingBusiness ? 'Update' : 'Create'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => { setShowConfirmModal(false); setBusinessToDelete(null); }}
          onConfirm={confirmDelete}
          title="Delete Business"
          message="Are you sure you want to delete this business? This will soft delete it."
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />
      </div>
    </Layout>
  );
};

export default Businesses;
